import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from './Layouts/Header';
import Footer from './Layouts/Footer';
import Sidebar from './Sidebar';
import { updateUserProfile, getUserProfile, fetchCountries, fetchStates } from '../utils/apiService';

const Editprofile = () => {
    const [formData, setFormData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        address: '',
        zipcode: '',
        city: '',
        state: '',
        country: ''
    });

    const [loading, setLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [successMessage, setSuccessMessage] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    
    // New state for countries and states
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [loadingCountries, setLoadingCountries] = useState(true);
    const [loadingStates, setLoadingStates] = useState(false);

    // Fetch countries on component mount
    useEffect(() => {
        const loadCountries = async () => {
            setLoadingCountries(true);
            const token = localStorage.getItem('token'); // Get token
            const result = await fetchCountries(token); // Pass token
            
            if (result && result.status && result.data) {
                setCountries(result.data);
            } else {
                console.error('Failed to fetch countries');
            }
            setLoadingCountries(false);
        };

        loadCountries();
    }, []);

    // Fetch user profile data on component mount
    useEffect(() => {
        const fetchUserData = async () => {
            const token = localStorage.getItem('token');

            if (!token) {
                console.error('No authentication token found');
                return;
            }

            const result = await getUserProfile(token);

            if (result && result.status) {
                const userData = result.data || result.user;
                setFormData({
                    firstName: userData.first_name || '',
                    lastName: userData.last_name || '',
                    email: userData.email || '',
                    phone: userData.phone || '',
                    address: userData.address || '',
                    zipcode: userData.zipcode || '',
                    city: userData.city || '',
                    state: userData.state || '',
                    country: userData.country || ''
                });

                // If user has a country, fetch its states
                if (userData.country) {
                    loadStatesForCountry(userData.country, token);
                }
            }
        };

        fetchUserData();
    }, []);

    // Function to load states based on country ID
    const loadStatesForCountry = async (countryId, token = null) => {
        if (!countryId) {
            setStates([]);
            return;
        }

        setLoadingStates(true);
        
        // Get token if not passed as parameter
        const authToken = token || localStorage.getItem('token');
        const result = await fetchStates(countryId, authToken); // Pass token
        
        if (result && result.status && result.data) {
            setStates(result.data);
        } else {
            console.error('Failed to fetch states');
            setStates([]);
        }
        setLoadingStates(false);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));

        // If country changes, fetch states for that country
        if (name === 'country') {
            setFormData(prev => ({
                ...prev,
                state: '' // Reset state when country changes
            }));
            const token = localStorage.getItem('token');
            loadStatesForCountry(value, token); // Pass token
        }

        // Clear error for this field when user starts typing
        if (errors[name]) {
            setErrors(prev => ({
                ...prev,
                [name]: ''
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrors({});
        setSuccessMessage('');
        setErrorMessage('');

        const token = localStorage.getItem('token');

        if (!token) {
            setErrorMessage('Authentication required. Please login again.');
            setLoading(false);
            return;
        }

        try {
            const result = await updateUserProfile(formData, token);

            if (result.status) {
                setSuccessMessage(result.message || 'Profile updated successfully!');

                const userData = result.data || result.user;
                if (userData) {
                    localStorage.setItem('userData', JSON.stringify(userData));
                }

                setTimeout(() => {
                    setSuccessMessage('');
                }, 5000);
            } else {
                setErrorMessage(result.message || 'Failed to update profile');
                if (result.errors) {
                    const mappedErrors = {};
                    Object.keys(result.errors).forEach(key => {
                        const frontendKey = key
                            .replace('first_name', 'firstName')
                            .replace('last_name', 'lastName');

                        mappedErrors[frontendKey] = Array.isArray(result.errors[key])
                            ? result.errors[key][0]
                            : result.errors[key];
                    });
                    setErrors(mappedErrors);
                }
            }
        } catch (error) {
            setErrorMessage('An unexpected error occurred. Please try again.');
            console.error('Profile update error:', error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="wrapper position-relative">
            <Header />

            <div className="page-title">
                <div className="tf-container">
                    <div className="page-title-content text-center">
                        <h1 className="title split-text effect-right">
                            Edit Profile
                        </h1>
                    </div>
                </div>
            </div>

            <div className="main-content">
                <section className="section-team p-team tf-spacing-2">
                    <div className="tf-container">
                        <div className="row rg-30">
                            <div className="col-lg-4 col-md-4">
                                <Sidebar />
                            </div>

                            <div className="col-lg-8 col-md-8">
                                <div className="section-contact-inner flex justify-content-between flex-wrap login-form">
                                    {successMessage && (
                                        <div className="alert alert-success w-100 mb-3" role="alert">
                                            {successMessage}
                                        </div>
                                    )}

                                    {errorMessage && (
                                        <div className="alert alert-danger w-100 mb-3" role="alert">
                                            {errorMessage}
                                        </div>
                                    )}

                                    <form
                                        id="editprofileform"
                                        className="form-contact-us style-bg-dark-2 px-md-15"
                                        onSubmit={handleSubmit}
                                    >
                                        <div className="cols mb-37 g-30">
                                            <fieldset className="item">
                                                <fieldset className="position-relative">
                                                    <i className="icon-user"></i>
                                                    <input
                                                        type="text"
                                                        name="firstName"
                                                        id="firstName"
                                                        placeholder="First Name"
                                                        value={formData.firstName}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                    {errors.firstName && (
                                                        <small className="text-danger">{errors.firstName}</small>
                                                    )}
                                                </fieldset>
                                            </fieldset>

                                            <fieldset className="item">
                                                <fieldset className="position-relative">
                                                    <i className="icon-user"></i>
                                                    <input
                                                        type="text"
                                                        name="lastName"
                                                        id="lastName"
                                                        placeholder="Last Name"
                                                        value={formData.lastName}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                    {errors.lastName && (
                                                        <small className="text-danger">{errors.lastName}</small>
                                                    )}
                                                </fieldset>
                                            </fieldset>
                                        </div>

                                        <div className="cols mb-37 g-30">
                                            <fieldset className="item">
                                                <fieldset className="position-relative">
                                                    <i className="icon-email"></i>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        id="email"
                                                        placeholder="Email Address"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                    {errors.email && (
                                                        <small className="text-danger">{errors.email}</small>
                                                    )}
                                                </fieldset>
                                            </fieldset>
                                        </div>

                                        <div className="cols mb-37 g-30">
                                            <fieldset className="item">
                                                <fieldset className="position-relative">
                                                    <i className="icon-phone"></i>
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        id="phone"
                                                        placeholder="Phone Number"
                                                        value={formData.phone}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                    {errors.phone && (
                                                        <small className="text-danger">{errors.phone}</small>
                                                    )}
                                                </fieldset>
                                            </fieldset>
                                        </div>

                                        <div className="cols mb-37 g-30">
                                            <fieldset className="item">
                                                <fieldset className="position-relative">
                                                    <i className="fa-regular fa-map"></i>
                                                    <input
                                                        type="text"
                                                        name="address"
                                                        id="address"
                                                        placeholder="Street Address"
                                                        value={formData.address}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                    {errors.address && (
                                                        <small className="text-danger">{errors.address}</small>
                                                    )}
                                                </fieldset>
                                            </fieldset>
                                        </div>

                                        <div className="cols mb-37 g-30">
                                            <fieldset className="item">
                                                <fieldset className="position-relative">
                                                    <i className="fa-solid fa-map-pin"></i>
                                                    <input
                                                        type="text"
                                                        name="zipcode"
                                                        id="zipcode"
                                                        placeholder="Zip/Postal Code"
                                                        value={formData.zipcode}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                    {errors.zipcode && (
                                                        <small className="text-danger">{errors.zipcode}</small>
                                                    )}
                                                </fieldset>
                                            </fieldset>

                                            {/* <fieldset className="item">
                                                <fieldset className="position-relative">
                                                    <i className="fa-solid fa-city"></i>
                                                    <input
                                                        type="text"
                                                        name="city"
                                                        id="city"
                                                        placeholder="City"
                                                        value={formData.city}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                    {errors.city && (
                                                        <small className="text-danger">{errors.city}</small>
                                                    )}
                                                </fieldset>
                                            </fieldset> */}
                                             <fieldset className="item">
                                                <select
                                                    name="country"
                                                    id="country"
                                                    className="nice-select w-100"
                                                    value={formData.country}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={loadingCountries}
                                                >
                                                    <option value="">
                                                        {loadingCountries ? 'Loading countries...' : 'Select Country'}
                                                    </option>
                                                    {countries.map(country => (
                                                        <option key={country.id} value={country.id}>
                                                            {country.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.country && (
                                                    <small className="text-danger">{errors.country}</small>
                                                )}
                                            </fieldset>
                                        </div>

                                        <div className="cols mb-37 g-30">
                                            {/* <fieldset className="item">
                                                <select
                                                    name="country"
                                                    id="country"
                                                    className="nice-select w-100"
                                                    value={formData.country}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={loadingCountries}
                                                >
                                                    <option value="">
                                                        {loadingCountries ? 'Loading countries...' : 'Select Country'}
                                                    </option>
                                                    {countries.map(country => (
                                                        <option key={country.id} value={country.id}>
                                                            {country.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.country && (
                                                    <small className="text-danger">{errors.country}</small>
                                                )}
                                            </fieldset> */}
                                            
                                            <fieldset className="item">
                                                <select
                                                    name="state"
                                                    id="state"
                                                    className="nice-select w-100"
                                                    value={formData.state}
                                                    onChange={handleChange}
                                                    required
                                                    disabled={!formData.country || loadingStates}
                                                >
                                                    <option value="">
                                                        {loadingStates 
                                                            ? 'Loading states...' 
                                                            : !formData.country 
                                                                ? 'Select country first' 
                                                                : 'Select State'
                                                        }
                                                    </option>
                                                    {states.map(state => (
                                                        <option key={state.id} value={state.id}>
                                                            {state.name}
                                                        </option>
                                                    ))}
                                                </select>
                                                {errors.state && (
                                                    <small className="text-danger">{errors.state}</small>
                                                )}
                                            </fieldset>
                                             <fieldset className="item">
                                                <fieldset className="position-relative">
                                                    <i className="fa-solid fa-city"></i>
                                                    <input
                                                        type="text"
                                                        name="city"
                                                        id="city"
                                                        placeholder="City"
                                                        value={formData.city}
                                                        onChange={handleChange}
                                                        required
                                                    />
                                                    {errors.city && (
                                                        <small className="text-danger">{errors.city}</small>
                                                    )}
                                                </fieldset>
                                            </fieldset>
                                        </div>

                                        <button
                                            type="submit"
                                            className="tf-btn style-bg-primary w-full justify-content-center mb-5"
                                            disabled={loading}
                                        >
                                            <span>{loading ? 'Updating...' : 'Update Profile'}</span>
                                            {!loading && <i className="icon-arrow-right"></i>}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
            <Footer />
        </div>
    );
};

export default Editprofile;